import apiClient, { ApiError } from "@/utils/httpClient";
import { getConfig } from "../../config.js";
import { AuthResponse, SessionData } from "@/interfaces/auth.interface.js";
import { setToken, removeToken } from "@/utils/token.js";

const SESSION_ID_KEY = "app_session_id";
export const storeSessionId = (id: string) => sessionStorage.setItem(SESSION_ID_KEY, id);
export const getStoredSessionId = () => sessionStorage.getItem(SESSION_ID_KEY);
export const clearSessionId = () => sessionStorage.removeItem(SESSION_ID_KEY);

/**
 * Validates the current session by calling the server.
 * Returns user/org data if the session cookie is still valid, null otherwise.
 */
export const validateSession = async (): Promise<SessionData | null> => {
  try {
    const response = await apiClient.getRawSilent<{ success: boolean; data: SessionData }>('/auth/me');
    if (response?.success && response.data) {
      return response.data;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Extends the current session by issuing a fresh token (same cookie flow).
 * Returns true on success, false if the session is already expired.
 */
export const refreshSession = async (): Promise<boolean> => {
  try {
    const response = await apiClient.postRaw<{ success: boolean }>('/auth/refresh');
    return response?.success === true;
  } catch {
    return false;
  }
};

/**
 * Logs out the current user
 * Clears the authentication token and calls the logout API
 */
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/logout');
    removeToken();
    clearSessionId();
  } catch (error) {
    console.error('Logout failed:', error);
    // Still remove token locally even if API call fails
    removeToken();
    clearSessionId();
    throw error;
  }
};

/**
 * Extracts a user-friendly error message from API error responses.
 * Handles both `{ message: "..." }` and `{ errors: [...] }` formats.
 */
function parseErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const b = body as Record<string, unknown>;
  if (typeof b.message === 'string') return b.message;
  if (Array.isArray(b.errors) && b.errors.length > 0) {
    const first = b.errors[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && typeof (first as Record<string, unknown>).message === 'string') return (first as Record<string, unknown>).message as string;
  }
  return fallback;
}

/**
 * Registers a new user.
 * Returns { email } — no JWT. User must verify email before logging in.
 */
export const register = async (firstName: string, lastName: string, email: string, password: string): Promise<{ email: string }> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ firstName, lastName, email, password }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Registration failed'), response.status);
  }

  const rawData = await response.json();
  return rawData.data ?? rawData;
};

/**
 * Verifies email with the token from the verification link.
 * Sets the auth cookie and token on success.
 */
export const verifyEmail = async (token: string): Promise<SessionData> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Email verification failed'), response.status);
  }

  const rawData = await response.json();
  const data: AuthResponse = rawData.data ?? rawData;
  if (data.token) {
    setToken(data.token);
  }
  if (data.sessionId) storeSessionId(data.sessionId);

  const session = await validateSession();
  return session ?? (data as unknown as SessionData);
};

/**
 * Sends a forgot password request. Always resolves (no info leak).
 */
export const forgotPassword = async (email: string): Promise<void> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Request failed'), response.status);
  }
};

/**
 * Resets password using the token from the reset link.
 */
export const resetPassword = async (token: string, password: string): Promise<void> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, password }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Password reset failed'), response.status);
  }
};

/**
 * Fetches invite information (org name, email) for a given invite token.
 */
export const getInviteInfo = async (token: string): Promise<{ organizationName: string; email: string }> => {
  const config = getConfig();
  const url = `${config.get('api.baseUrl')}/auth/invite-info?token=${encodeURIComponent(token)}`;
  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Failed to load invite info'), response.status);
  }

  const rawData = await response.json();
  return rawData.data ?? rawData;
};

/**
 * Accepts a team invite. If newUserData is provided, creates a new account.
 */
export const acceptInvite = async (
  token: string,
  newUserData?: { firstName: string; lastName: string; password: string }
): Promise<SessionData> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/accept-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, ...newUserData }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Failed to accept invite'), response.status);
  }

  const rawData = await response.json();
  const data: AuthResponse = rawData.data ?? rawData;
  if (data.token) {
    setToken(data.token);
  }
  if (data.sessionId) storeSessionId(data.sessionId);

  const session = await validateSession();
  return session ?? (data as unknown as SessionData);
};

/**
 * Resends email verification. Always resolves (no info leak).
 */
export const resendVerification = async (email: string): Promise<void> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Request failed'), response.status);
  }
};

/**
 * Sends the QuickBooks OAuth callback URL to the API to complete the OAuth flow.
 * Returns the user and organization data from the session.
 */
export const quickbooksCallback = async (): Promise<{ user: unknown; organization: unknown }> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ url: window.location.href }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'QuickBooks authentication failed'), response.status);
  }

  const rawData = await response.json();
  const data = rawData.data ?? rawData;

  if (data.token) {
    setToken(data.token);
  }

  return {
    user: data.user,
    organization: data.organization,
  };
};

/**
 * Logs in a user with email and password.
 * Uses raw fetch to avoid the httpClient 401 redirect on auth endpoints.
 */
export const login = async (email: string, password: string): Promise<SessionData> => {
  const config = getConfig();
  const response = await fetch(`${config.get('api.baseUrl')}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(parseErrorMessage(body, 'Login failed'), response.status);
  }

  const rawData = await response.json();
  const data = rawData.data ?? rawData;
  if (!data.token) {
    throw new ApiError('Login failed — no token received');
  }

  setToken(data.token);
  if (data.sessionId) storeSessionId(data.sessionId);

  const loginSyncQueued: boolean = data.loginSyncQueued === true;

  // The login response only contains user + token, not organization/integration info.
  // Fetch the full session data from /auth/me to determine onboarding status.
  const session = await validateSession();
  if (session) {
    return { ...session, loginSyncQueued };
  }

  // Fallback: return what we have (will trigger onboarding flow)
  return data as SessionData;
};
